/**
 * PEC connectors — Italian Posta Elettronica Certificata.
 *
 * Per CLAUDE.md memory rule (`feedback_white_label_capability_naming.md`),
 * PEC is the EXPLICIT exception to white-label naming: `aruba_pec` and
 * `infocert_pec` are vendor-named slugs because the legal landscape forces
 * the user to know which provider issued their PEC mailbox.
 *
 * ── May 24, 2026 — split + factory DRY ─────────────────────────────────
 *
 * Previous version exposed a single `pecSpec` with `capability: "pec"`
 * and a vendor_picker step. The BE never had a `pec` capability — only
 * `aruba_pec` and `infocert_pec` — so every call to
 * `GET /connectors/pec/connections` 404'd and the wizard silently
 * couldn't start the flow.
 *
 * Now each provider gets its own spec via a `createPecSpec` factory
 * that owns the 90% shared shape (icon, benefits, gdprDeleteWindowMinutes,
 * connectionKeyField, getDisplayName, getSecondaryId, single-entry
 * vendors). Each call site only declares the 10% delta — capability
 * slug, title, subtitle, and the auth `steps` (creds vs OAuth).
 *
 * Drift prevention: the previous parallel-spec layout meant bumping a
 * GDPR window on Aruba would leave InfoCert behind. The factory makes
 * that bug-class impossible.
 */

import {
  type BaseConnection,
  type CapabilityWizardSpec,
  type LocalizedText,
  localize,
} from "@ando/ui";
import { i18n as i18nSingleton } from "@lingui/core";
import { msg } from "@lingui/core/macro";
import { Mail, Scale, Shield, Stamp } from "lucide-react";

import { createCapabilityApi } from "../api/capabilityApi";
import { vendorIcon } from "../components/vendorIcons";

export interface PecConnection extends BaseConnection {
  readonly provider?: "aruba_pec" | "infocert_pec";
  readonly pec_address?: string;
}

const iconCls = "h-4 w-4";

const PEC_BENEFITS: CapabilityWizardSpec<PecConnection>["benefits"] = [
  {
    icon: <Mail className={iconCls} aria-hidden />,
    title: msg`PEC in chat`,
    body: msg`Ricevute, originali e firmati pronti per ricerca naturale.`,
  },
  {
    icon: <Stamp className={iconCls} aria-hidden />,
    title: msg`Ricevute valide`,
    body: msg`Firma digitale e marca temporale preservate dall'archivio.`,
  },
  {
    icon: <Scale className={iconCls} aria-hidden />,
    title: msg`Valore legale`,
    body: msg`Le PEC restano consultabili come prova in caso di contenzioso.`,
  },
  {
    icon: <Shield className={iconCls} aria-hidden />,
    title: msg`Conformità`,
    body: msg`Nessuna re-inoltro automatico — Ando legge, l'utente decide.`,
  },
];

type PecCapability = "aruba_pec" | "infocert_pec";

interface CreatePecSpecOptions {
  readonly capability: PecCapability;
  readonly title: LocalizedText;
  readonly subtitle: LocalizedText;
  readonly steps: CapabilityWizardSpec<PecConnection>["steps"];
}

function createPecSpec(opts: CreatePecSpecOptions): CapabilityWizardSpec<PecConnection> {
  return {
    capability: opts.capability,
    title: opts.title,
    subtitle: opts.subtitle,
    icon: <Stamp className="h-5 w-5" aria-hidden />,
    // 30min: aligned with accounting/SDI/cassetto baseline.
    gdprDeleteWindowMinutes: 30,
    // Single-vendor capability — `vendors` carries one entry so the
    // wizard skips the picker step.
    vendors: [{ id: opts.capability, label: opts.title, icon: vendorIcon(opts.capability, iconCls) }],
    steps: opts.steps,
    benefits: PEC_BENEFITS,
    api: createCapabilityApi<PecConnection>({ capability: opts.capability }),
    connectionKeyField: "id" as keyof PecConnection,
    getDisplayName: (c) => c.pec_address ?? c.id ?? localize(opts.title, i18nSingleton),
    // Secondary id: surface a friendly "Provider" label, NOT the raw
    // capability slug (previously rendered the literal "aruba_pec" /
    // "infocert_pec" under the display name — confusing UX).
    getSecondaryId: () => localize(opts.title, i18nSingleton),
  };
}

// ── Aruba PEC — IMAP/SMTP app-password flow ─────────────────────────────
export const arubaPecSpec: CapabilityWizardSpec<PecConnection> = createPecSpec({
  capability: "aruba_pec",
  title: "Aruba PEC",
  subtitle: msg`Posta Elettronica Certificata via IMAP/SMTP (app password)`,
  steps: [
    {
      id: "creds",
      kind: "credentials",
      title: msg`Credenziali Aruba PEC`,
      description: msg`Genera una app password dal pannello cliente Aruba e usala qui — Ando non vede mai la tua password principale.`,
      fields: [
        {
          name: "pec_address",
          label: msg`Indirizzo PEC`,
          type: "email",
          required: true,
          placeholder: "mario.rossi@pec.acme.it",
        },
        {
          name: "password",
          label: msg`App password`,
          type: "password",
          required: true,
        },
      ],
    },
  ],
});

// ── InfoCert PEC (Legalmail) — IMAP/SMTP app-password flow ──────────────
// InfoCert Legalmail is standard IMAPS/SMTPS (mbox/sendm.cert.legalmail.it),
// NOT OAuth — same credentials shape as Aruba PEC (full PEC address + app
// password). This keeps connect ⇄ send ⇄ sync consistent (all credential-based).
export const infocertPecSpec: CapabilityWizardSpec<PecConnection> = createPecSpec({
  capability: "infocert_pec",
  title: "InfoCert PEC",
  subtitle: msg`Posta Elettronica Certificata via IMAP/SMTP (app password)`,
  steps: [
    {
      id: "creds",
      kind: "credentials",
      title: msg`Credenziali InfoCert PEC`,
      description: msg`Genera una app password dal pannello Legalmail e usala qui — Ando non vede mai la tua password principale.`,
      fields: [
        {
          name: "pec_address",
          label: msg`Indirizzo PEC`,
          type: "email",
          required: true,
          placeholder: "mario.rossi@legalmail.it",
        },
        {
          name: "password",
          label: msg`App password`,
          type: "password",
          required: true,
        },
      ],
    },
  ],
});
